import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { Input } from "./ui/input";
import { getColleges } from "../lib/api";

// Same search behaviour as the Colleges page (debounced 350ms, fires at 3+
// chars, hits the same `/colleges?search=` endpoint).
// Desktop: compact inline input with a live dropdown of top matches.
// Mobile: a tappable trigger that pops open a full-screen, centred Google-style
// search overlay with the live results list.
export const HeaderCollegeSearch = ({ className = "", testId = "header-college-search" }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      getColleges({ search: q, limit: 6 })
        .then((r) => { setResults(r.data.colleges || []); setOpen(true); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onOutside = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  // Lock background scroll while the mobile overlay is open
  useEffect(() => {
    if (overlayOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [overlayOpen]);

  const closeAll = () => { setOpen(false); setOverlayOpen(false); };

  const goToAllResults = () => {
    if (!query.trim()) return;
    closeAll();
    navigate(`/colleges?q=${encodeURIComponent(query.trim())}`);
  };

  const goToCollege = (c) => {
    closeAll(); setQuery("");
    navigate(c.seo_path ? `/colleges/${c.seo_path}` : `/colleges/${c.college_code || c.id}`);
  };

  const ResultsList = ({ variant }) => {
    const q = query.trim();
    if (q.length < 3) {
      return variant === "overlay"
        ? <p className="p-8 text-center text-sm text-zinc-400" data-testid={`${testId}-overlay-hint`}>Type at least 3 letters to search colleges…</p>
        : null;
    }
    if (loading) return <div className={variant === "overlay" ? "p-8 text-center text-sm text-zinc-400" : "px-3 py-3 text-xs text-zinc-400"}>Searching…</div>;
    if (results.length === 0) return <div className={variant === "overlay" ? "p-8 text-center text-sm text-zinc-400" : "px-3 py-3 text-xs text-zinc-400"}>No colleges found</div>;
    const big = variant === "overlay";
    return (
      <>
        {results.map((c) => (
          <button
            key={c.id || c.college_code}
            onClick={() => goToCollege(c)}
            className={`w-full text-left border-b border-zinc-100 last:border-0 ${big ? "px-4 py-3 active:bg-zinc-50" : "px-3 py-2 hover:bg-zinc-50"}`}
            data-testid={`${testId}-result-${c.id || c.college_code}`}
          >
            <div className={`font-bold text-[#09090B] truncate ${big ? "text-sm" : "text-xs"}`}>{c.name}</div>
            <div className={`text-zinc-500 truncate ${big ? "text-xs" : "text-[11px]"}`}>{c.state}{c.location ? ` · ${c.location}` : ""}</div>
          </button>
        ))}
        <button
          onClick={goToAllResults}
          className={`w-full text-left font-bold uppercase tracking-wider text-[#6043EF] hover:bg-[#6043EF]/5 ${big ? "px-4 py-3 text-xs" : "px-3 py-2 text-[11px]"}`}
          data-testid={`${testId}-view-all`}
        >
          View all results for "{query.trim()}" →
        </button>
      </>
    );
  };

  return (
    <>
      {/* Desktop / tablet — inline input with dropdown */}
      <div ref={boxRef} className={`relative hidden sm:block ${className}`} data-testid={testId}>
        <form onSubmit={(e) => { e.preventDefault(); goToAllResults(); }} className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setOpen(true)}
            placeholder="Search colleges..."
            className="pl-8 h-9 text-sm border-zinc-300 focus:border-[#6043EF]"
            data-testid={`${testId}-input`}
          />
        </form>
        {open && query.trim().length >= 3 && (
          <div className="absolute top-full left-0 mt-1 w-full min-w-[280px] bg-white border border-zinc-200 shadow-lg z-50" data-testid={`${testId}-dropdown`}>
            <ResultsList variant="dropdown" />
          </div>
        )}
      </div>

      {/* Mobile — tap-to-open trigger */}
      <button
        type="button"
        onClick={() => setOverlayOpen(true)}
        className={`sm:hidden flex items-center gap-2 h-9 px-3 rounded-md border border-zinc-300 bg-white text-zinc-400 text-sm ${className}`}
        data-testid={`${testId}-mobile-trigger`}
        aria-label="Search colleges"
      >
        <MagnifyingGlass size={16} className="shrink-0" />
        <span className="truncate">Search colleges...</span>
      </button>

      {/* Mobile — pop-out search modal over a dimmed, see-through page */}
      {overlayOpen && (
        <div
          className="sm:hidden fixed inset-0 z-[100] bg-black/40 backdrop-blur-[2px] animate-fade-in flex justify-center items-start pt-16 px-3"
          data-testid={`${testId}-overlay`}
          onClick={closeAll}
        >
          <div
            className="w-full max-w-[440px] rounded-2xl overflow-hidden animate-modal-pop bg-gradient-to-b from-white to-zinc-100/90 ring-1 ring-black/5 border border-white/70 shadow-[0_24px_70px_-15px_rgba(96,67,239,0.55),0_12px_28px_-12px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.9)]"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testId}-overlay-panel`}
          >
            <div className="flex items-center gap-2 p-3 border-b border-white/60 bg-gradient-to-b from-[#6043EF]/[0.06] to-transparent">
              <div className="relative flex-1">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  autoFocus
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") goToAllResults(); }}
                  placeholder="Search colleges..."
                  className="w-full h-11 pl-10 pr-3 text-base bg-white rounded-full border border-zinc-200 shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)] focus:border-[#6043EF] focus:ring-2 focus:ring-[#6043EF]/20 focus:outline-none"
                  data-testid={`${testId}-overlay-input`}
                />
              </div>
              <button
                onClick={closeAll}
                className="w-10 h-10 flex items-center justify-center text-[#09090B] shrink-0"
                data-testid={`${testId}-overlay-close`}
                aria-label="Close search"
              >
                <X size={22} weight="bold" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto" data-testid={`${testId}-overlay-results`}>
              <ResultsList variant="overlay" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
